import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Loader2, Trophy, ChevronLeft, ChevronRight, Gamepad2, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import MultipleChoiceActivity from "./MultipleChoiceActivity";
import DragDropActivity from "./DragDropActivity";
import OrderingActivity from "./OrderingActivity";
import FillBlankActivity from "./FillBlankActivity";
import LessonRating from "./LessonRating";

interface ActivityPlayerProps {
  lessonId: string;
  childId: string;
  nurseryId: string;
}

const ActivityPlayer = ({ lessonId, childId, nurseryId }: ActivityPlayerProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState<Record<string, { isCorrect: boolean; score: number }>>({});
  const [showSummary, setShowSummary] = useState(false);
  const startTimeRef = useRef<number>(Date.now());

  const { data: activities, isLoading } = useQuery({
    queryKey: ["lesson-activities", lessonId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lesson_activities")
        .select("*")
        .eq("lesson_id", lessonId)
        .order("sort_order");
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch existing responses
  const { data: existingResponses } = useQuery({
    queryKey: ["activity-responses", lessonId, childId],
    queryFn: async () => {
      if (!activities?.length) return [];
      const { data } = await supabase
        .from("activity_responses")
        .select("*")
        .eq("child_id", childId)
        .in("activity_id", activities.map(a => a.id));
      return data || [];
    },
    enabled: !!activities?.length && !!childId,
  });

  // Fetch existing rating
  const { data: existingRating } = useQuery({
    queryKey: ["lesson-rating", lessonId, childId],
    queryFn: async () => {
      const { data } = await supabase
        .from("lesson_ratings")
        .select("*")
        .eq("lesson_id", lessonId)
        .eq("child_id", childId)
        .maybeSingle();
      return data;
    },
  });

  useEffect(() => {
    startTimeRef.current = Date.now();
  }, [currentIndex]);

  const handleAnswer = async (activityId: string, isCorrect: boolean, maxScore: number) => {
    const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
    const score = isCorrect ? maxScore : 0;
    setResults(prev => ({ ...prev, [activityId]: { isCorrect, score } }));

    // Save to DB
    await supabase.from("activity_responses").upsert({
      activity_id: activityId,
      child_id: childId,
      nursery_id: nurseryId,
      answer: { isCorrect },
      is_correct: isCorrect,
      score,
      time_spent_seconds: timeSpent,
    }, { onConflict: "activity_id,child_id" });
  };

  const handleRate = async (rating: number, emoji: string) => {
    await supabase.from("lesson_ratings").upsert({
      lesson_id: lessonId,
      child_id: childId,
      nursery_id: nurseryId,
      rating,
      emoji,
    }, { onConflict: "lesson_id,child_id" });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!activities?.length) {
    return (
      <Card className="shadow-card">
        <CardContent className="py-12 text-center">
          <Gamepad2 className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">لا توجد أنشطة تفاعلية لهذا الدرس</p>
        </CardContent>
      </Card>
    );
  }

  const totalActivities = activities.length;
  const progress = (Object.keys(results).length / totalActivities) * 100;
  const currentActivity = activities[currentIndex];
  const content = currentActivity?.content as any;
  const alreadyAnswered = existingResponses?.find(r => r.activity_id === currentActivity?.id);
  const isAnswered = !!results[currentActivity?.id] || !!alreadyAnswered;

  if (showSummary) {
    const totalScore = Object.values(results).reduce((sum, r) => sum + r.score, 0);
    const maxTotal = activities.reduce((sum, a) => sum + (a.max_score || 10), 0);
    const percentage = Math.round((totalScore / maxTotal) * 100);
    const stars = percentage >= 90 ? 3 : percentage >= 60 ? 2 : percentage >= 30 ? 1 : 0;

    return (
      <Card className="shadow-card overflow-hidden">
        <div className="bg-gradient-to-br from-primary/20 via-secondary/10 to-primary/5 p-8">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-center space-y-4">
            <Trophy className="w-16 h-16 text-yellow-500 mx-auto" />
            <h2 className="text-2xl font-bold text-foreground">أحسنت! أكملت جميع الأنشطة 🎉</h2>
            <div className="flex items-center justify-center gap-1">
              {[1, 2, 3].map(s => (
                <Star key={s} className={cn("w-8 h-8", s <= stars ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground/20")} />
              ))}
            </div>
            <div className="text-4xl font-bold text-primary">{percentage}%</div>
            <p className="text-muted-foreground">{totalScore} من {maxTotal} نقطة</p>
            <div className="pt-4">
              <LessonRating onRate={handleRate} existingRating={existingRating?.rating} />
            </div>
          </motion.div>
        </div>
      </Card>
    );
  }

  const renderActivity = () => {
    if (!currentActivity) return null;
    const type = currentActivity.activity_type;

    switch (type) {
      case "multiple_choice":
        return (
          <MultipleChoiceActivity
            question={content.question}
            options={content.options || []}
            correctId={content.correctId}
            onAnswer={(_, isCorrect) => handleAnswer(currentActivity.id, isCorrect, currentActivity.max_score || 10)}
            disabled={!!alreadyAnswered}
          />
        );
      case "drag_drop":
        return (
          <DragDropActivity
            question={content.question}
            items={content.items || []}
            zones={content.zones || []}
            onAnswer={(_, isCorrect) => handleAnswer(currentActivity.id, isCorrect, currentActivity.max_score || 10)}
            disabled={!!alreadyAnswered}
          />
        );
      case "ordering":
        return (
          <OrderingActivity
            question={content.question}
            items={content.items || []}
            correctOrder={content.correctOrder || []}
            onAnswer={(_, isCorrect) => handleAnswer(currentActivity.id, isCorrect, currentActivity.max_score || 10)}
            disabled={!!alreadyAnswered}
          />
        );
      case "fill_blank":
        return (
          <FillBlankActivity
            question={content.question}
            correctAnswer={content.correctAnswer || ""}
            hint={content.hint}
            acceptableAnswers={content.acceptableAnswers}
            onAnswer={(_, isCorrect) => handleAnswer(currentActivity.id, isCorrect, currentActivity.max_score || 10)}
            disabled={!!alreadyAnswered}
          />
        );
      default:
        return <p className="text-center text-muted-foreground">نوع نشاط غير معروف</p>;
    }
  };

  return (
    <Card className="shadow-card overflow-hidden">
      <CardHeader className="pb-3 border-b bg-muted/30">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Gamepad2 className="w-4 h-4 text-primary" />
            {currentActivity.title}
          </CardTitle>
          <Badge variant="outline" className="text-xs">
            {currentIndex + 1} / {totalActivities}
          </Badge>
        </div>
        <Progress value={progress} className="h-2 mt-2" />
      </CardHeader>
      <CardContent className="p-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentActivity.id}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
          >
            {renderActivity()}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentIndex(i => i - 1)}
            disabled={currentIndex === 0}
            className="gap-1"
          >
            <ChevronRight className="w-4 h-4" />
            السابق
          </Button>
          {currentIndex < totalActivities - 1 ? (
            <Button
              size="sm"
              onClick={() => setCurrentIndex(i => i + 1)}
              disabled={!isAnswered}
              className="gap-1"
            >
              التالي
              <ChevronLeft className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => setShowSummary(true)}
              disabled={!isAnswered}
              className="gap-1 bg-green-600 hover:bg-green-700"
            >
              <Trophy className="w-4 h-4" />
              إنهاء
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ActivityPlayer;
